// The people on a service: position, person, status, remove; plus the inline
// "+ Add person" row. People on leave or away that day can't be picked.
import { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { AvailabilityStatusValue, TeamMemberAssignment, User } from '@app/types';
import { Avatar, Button, Input, Menu, MenuItem, Select, StatusPill, cx } from '@app/components/ui';
import { TEAM_DEFINITIONS } from '../../utils/rosterHelpers';

export const POSITIONS: string[] = TEAM_DEFINITIONS.flatMap((t) => t.roles);
const STATUS_LABEL = ['Pending', 'Confirmed', 'Declined'] as const;

function StatusButton({ status, disabled, onChange, name }: { status: number; disabled?: boolean; onChange: (s: number) => void; name: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const label = STATUS_LABEL[status] ?? 'Pending';
  if (disabled) return <StatusPill status={label} />;
  return (
    <>
      <button
        ref={ref}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${name}: ${label}. Change status`}
        onClick={() => setOpen(!open)}
        className="rounded-full shrink-0"
      >
        <StatusPill status={label} />
      </button>
      <Menu open={open} onClose={() => setOpen(false)} anchorRef={ref} label="Status" width={180} align="end">
        {STATUS_LABEL.map((l, i) => (
          <MenuItem
            key={l}
            onSelect={() => {
              setOpen(false);
              if (i !== status) onChange(i);
            }}
            hint={i === status ? '✓' : undefined}
          >
            Mark {l.toLowerCase()}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function TeamSlots({
  team,
  readOnly,
  members,
  availabilityByUser,
  onLeaveUserIds,
  onSwitchRole,
  onRemove,
  onUpdateStatus,
  onAdd,
  onAddPlaceholder,
}: {
  team: TeamMemberAssignment[];
  readOnly: boolean;
  members: User[];
  availabilityByUser: Record<string, AvailabilityStatusValue>;
  onLeaveUserIds: Set<string>;
  onSwitchRole: (userId: string, role: string) => void;
  onRemove: (userId: string) => void;
  onUpdateStatus: (userId: string, status: number) => void;
  onAdd: (userId: string, role: string) => void;
  onAddPlaceholder: (name: string, role: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [guest, setGuest] = useState(false);
  const [member, setMember] = useState('');
  const [guestName, setGuestName] = useState('');
  const [role, setRole] = useState('');

  const assigned = useMemo(() => new Set(team.map((t) => t.userId)), [team]);
  const memberOptions = useMemo(
    () =>
      members
        .filter((m) => !assigned.has(m.id))
        .map((m) => {
          const leave = onLeaveUserIds.has(m.id);
          const avail = availabilityByUser[m.id];
          const away = !leave && avail === 'Blocked';
          return {
            value: m.id,
            label: m.username,
            sub: avail === 'Tentative' ? 'Tentative that day' : m.email,
            disabled: leave || away,
            tag: leave ? 'On leave' : away ? 'Away' : undefined,
          };
        })
        .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled) || a.label.localeCompare(b.label)),
    [members, assigned, onLeaveUserIds, availabilityByUser],
  );
  const roleOptions = (current?: string) => {
    const list = current && !POSITIONS.includes(current) ? [current, ...POSITIONS] : POSITIONS;
    return list.map((p) => ({ value: p, label: p }));
  };

  const reset = () => {
    setAdding(false);
    setGuest(false);
    setMember('');
    setGuestName('');
    setRole('');
  };

  const confirmAdd = () => {
    const r = role || 'Worship Leader';
    if (guest) {
      if (!guestName.trim()) return;
      onAddPlaceholder(guestName.trim(), r);
    } else {
      if (!member) return;
      onAdd(member, r);
    }
    reset();
  };

  return (
    <div>
      {team.length === 0 && <p className="text-[13.5px] text-t4 py-3 border-t border-line">No one is on this service yet.</p>}
      {team.map((t) => (
        <div key={t.id} className="flex items-center gap-2.5 py-[9px] border-t border-line">
          <Avatar name={t.name} size={30} />
          <div className="flex-1 min-w-0">
            <div className={cx('text-[13.5px] font-medium truncate', t.name ? 'text-t1' : 'text-t4')}>{t.name || 'Unfilled'}</div>
            {(readOnly || t.isPlaceholder) && (
              <div className="text-[12px] text-t4 truncate">
                {readOnly ? t.role : 'Hasn’t joined the app yet'}
              </div>
            )}
          </div>
          {!readOnly && (
            <div className="w-[136px] shrink-0">
              <Select size="sm" aria-label={`${t.name || 'Slot'} position`} value={t.role} options={roleOptions(t.role)} onChange={(v) => onSwitchRole(t.userId, v)} />
            </div>
          )}
          <StatusButton status={t.status} disabled={readOnly || t.isPlaceholder} onChange={(s) => onUpdateStatus(t.userId, s)} name={t.name} />
          {!readOnly && (
            <button
              type="button"
              onClick={() => onRemove(t.userId)}
              aria-label={`Remove ${t.name || t.role} from service`}
              className="w-[26px] h-[26px] rounded-full text-t4 hover:bg-acc-bg hover:text-acc flex items-center justify-center shrink-0"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}

      {!readOnly &&
        (adding ? (
          <div className="mt-2 p-3 rounded-[14px] bg-fill flex flex-col gap-2 wp-fade">
            {guest ? (
              <Input
                aria-label="Guest name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Guest's name, e.g. John Smith"
                className="!bg-card"
                data-autofocus
              />
            ) : (
              <Select aria-label="Person" value={member} onChange={setMember} options={memberOptions} placeholder="Choose a person" />
            )}
            <Select aria-label="Position" value={role} onChange={setRole} options={roleOptions()} placeholder="Position" />
            <div className="text-[12px] text-t4 flex flex-wrap justify-between gap-2">
              <span>{guest ? 'For someone who hasn’t joined the app yet.' : 'People on leave or away that day can’t be picked.'}</span>
              <button type="button" className="text-link" onClick={() => setGuest(!guest)}>
                {guest ? 'Choose a member instead' : 'Add a guest by name'}
              </button>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="md" className="!bg-card !h-[38px]" onClick={reset}>
                Cancel
              </Button>
              <Button variant="dark" size="md" className="flex-1 !h-[38px]" onClick={confirmAdd} disabled={guest ? !guestName.trim() : !member}>
                Add to service
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="w-full h-10 mt-2 rounded-[12px] border border-dashed border-line2 text-t2 hover:text-t1 hover:border-t5 text-[13.5px] font-medium"
          >
            + Add person
          </button>
        ))}
    </div>
  );
}
